import { el, progress, example, resultCard } from '../dom.js';
import { copy } from '../copy.js';
import { createCaptureInput, fileToObjectURL, revokeObjectURL } from '../camera.js';

export function renderModem(state, actions) {
  return renderPhotoStep({
    state,
    actions,
    step: 1,
    total: 4,
    slot: 'modem',
    copy: copy.modem,
    exampleSrc: '/assets/examples/modem-example.jpg',
    nextScreen: 'gateway',
    photo: state.photos.modem,
  });
}

// Shared helper used by the modem + gateway screens. Both are "one photo
// of one labeled box, react to what the VLM recognized." The AP screen is
// a multi-photo loop and has its own file.
export function renderPhotoStep({
  state,
  actions,
  step,
  total,
  slot,
  copy: screenCopy,
  exampleSrc,
  nextScreen,
  photo,
}) {
  const { goto, submitPhoto, clearError, clearSlot } = actions;

  let pendingFile = null;
  let pendingUrl = null;

  const screen = el('div', { class: 'screen' });
  screen.appendChild(progress({ step, total }));
  screen.appendChild(el('h1', { text: screenCopy.title }));
  screen.appendChild(el('p', { text: screenCopy.body }));
  if (screenCopy.stickerHint) {
    screen.appendChild(el('p', { class: 'hint', text: screenCopy.stickerHint }));
  }
  // Hide the "here's a good photo" example once we're showing the user
  // their own capture. Keeps the screen from feeling cluttered.
  const showExample = !photo && !pendingFile;
  if (showExample && exampleSrc) {
    screen.appendChild(
      example({
        label: screenCopy.exampleLabel,
        src: exampleSrc,
        caption: screenCopy.exampleCaption,
      }),
    );
  }

  const bodyWrap = el('div', {});
  screen.appendChild(bodyWrap);

  const errorWrap = el('div', {});
  if (state.error) {
    errorWrap.appendChild(el('div', { class: 'session-error', text: state.error }));
  }
  screen.appendChild(errorWrap);

  const actionsWrap = el('div', { class: 'actions' });
  screen.appendChild(actionsWrap);

  const captureInput = createCaptureInput({
    onPick: (file) => {
      pendingFile = file;
      if (pendingUrl) revokeObjectURL(pendingUrl);
      pendingUrl = fileToObjectURL(file);
      renderActions();
    },
  });
  screen.appendChild(captureInput);

  function startRetake() {
    pendingFile = null;
    if (pendingUrl) revokeObjectURL(pendingUrl);
    pendingUrl = null;
    clearSlot(slot);
    captureInput.click();
  }

  function renderActions() {
    bodyWrap.replaceChildren();
    actionsWrap.replaceChildren();

    if (state.uploading) {
      actionsWrap.appendChild(
        el('div', { class: 'uploading' }, [el('div', { class: 'spinner' }), copy.uploading]),
      );
      return;
    }

    // Post-upload state: we have a server-side result for this slot.
    if (photo) {
      bodyWrap.appendChild(
        resultCard({
          recognized: photo.recognized,
          extraction: photo.extraction,
          resultCopy: copy.result,
        }),
      );

      if (photo.recognized) {
        actionsWrap.appendChild(
          el('button', {
            class: 'primary',
            text: screenCopy.continue,
            onClick: () => goto(nextScreen),
          }),
        );
        actionsWrap.appendChild(
          el('button', {
            class: 'secondary',
            text: screenCopy.retake,
            onClick: startRetake,
          }),
        );
      } else {
        actionsWrap.appendChild(
          el('button', {
            class: 'primary',
            text: screenCopy.retake,
            onClick: startRetake,
          }),
        );
        actionsWrap.appendChild(
          el('button', {
            class: 'skip',
            text: screenCopy.skip,
            onClick: () => goto(nextScreen),
          }),
        );
      }
      return;
    }

    // Pre-upload with a preview: the user just took a photo and is
    // deciding whether to send it for recognition.
    if (pendingFile && pendingUrl) {
      bodyWrap.appendChild(el('img', { class: 'preview', src: pendingUrl, alt: 'Preview' }));
      actionsWrap.appendChild(
        el('button', {
          class: 'primary',
          text: screenCopy.usePhoto,
          onClick: async () => {
            clearError();
            await submitPhoto({ slot, file: pendingFile });
            pendingFile = null;
            if (pendingUrl) revokeObjectURL(pendingUrl);
            pendingUrl = null;
          },
        }),
      );
      actionsWrap.appendChild(
        el('button', {
          class: 'secondary',
          text: screenCopy.retake,
          onClick: () => {
            pendingFile = null;
            if (pendingUrl) revokeObjectURL(pendingUrl);
            pendingUrl = null;
            captureInput.click();
          },
        }),
      );
      return;
    }

    // Cold-start: no photo yet.
    actionsWrap.appendChild(
      el('button', {
        class: 'primary',
        text: screenCopy.take,
        onClick: () => captureInput.click(),
      }),
    );
    actionsWrap.appendChild(
      el('button', {
        class: 'skip',
        text: screenCopy.skip,
        onClick: () => goto(nextScreen),
      }),
    );
  }

  renderActions();
  return screen;
}
